import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaUserGraduate, FaBook, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { fetchAPI } from '../utils/api';

export default function Dashboard() {
    const { currentUser } = useAuth();
    const role = currentUser?.role;
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadActivities = async () => {
            if (!currentUser) return;
            try {
                // Dashboard home should always show personal activity for the user
                const data = await fetchAPI(`/student/get_recent_activity.php?user_id=${currentUser.id}`);
                setActivities(data);
            } catch (err) {
                console.error("Failed to load activities", err);
            } finally {
                setLoading(false);
            }
        };
        loadActivities();
    }, [currentUser]);

    return (
        <div className="relative min-h-screen overflow-hidden rounded-3xl">
            {/* Background Decorations (Cloud/Sky effect) - Matching Login/Register */}
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-50 via-white to-blue-50 opacity-50"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

            <div className="relative z-10 space-y-6 p-1"> {/* p-1 to prevent margin collapse issues if any */}

                {/* Welcome Card */}
                <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Welcome back, <span className="text-blue-600">{currentUser?.name?.split(' ')[0]}</span>!
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Here's an overview of your learning progress and upcoming tasks.
                    </p>
                </div>

                {/* Role-Specific Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {role === 'admin' && (
                        <>
                            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 hover:bg-white/80 transition-all duration-300">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Students</p>
                                        <h3 className="text-2xl font-bold text-gray-900 mt-2">1,234</h3>
                                    </div>
                                    <div className="p-3 bg-blue-100/50 rounded-xl text-blue-600">
                                        <FaUserGraduate className="text-xl" />
                                    </div>
                                </div>
                            </div>
                            {/* Add more admin widgets here using the same style */}
                        </>
                    )}

                    {role === 'student' && (
                        <>
                            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 hover:bg-white/80 transition-all duration-300">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Enrolled Courses</p>
                                        <h3 className="text-2xl font-bold text-gray-900 mt-2">4</h3>
                                    </div>
                                    <div className="p-3 bg-purple-100/50 rounded-xl text-purple-600">
                                        <FaBook className="text-xl" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 hover:bg-white/80 transition-all duration-300">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Average Grade</p>
                                        <h3 className="text-2xl font-bold text-gray-900 mt-2">A</h3>
                                    </div>
                                    <div className="p-3 bg-green-100/50 rounded-xl text-green-600">
                                        <FaCheckCircle className="text-xl" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/60 backdrop-blur-xl rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 hover:bg-white/80 transition-all duration-300">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment Status</p>
                                        <h3 className="text-2xl font-bold text-red-500 mt-2">Due</h3>
                                    </div>
                                    <div className="p-3 bg-red-100/50 rounded-xl text-red-600">
                                        <FaExclamationTriangle className="text-xl" />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Main Content Area / Recent Activity */}
                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Activity */}
                    <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6">
                        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            Recent Activity
                        </h2>
                        <div className="space-y-6 pl-2">
                            {loading ? (
                                <p className="text-sm text-gray-400 italic">Loading activity...</p>
                            ) : activities.length === 0 ? (
                                <p className="text-sm text-gray-400 italic">No recent activity</p>
                            ) : activities.map((log, idx) => (
                                <div key={log.id} className={`relative pl-6 border-l-2 border-dashed ${idx === activities.length - 1 ? 'border-transparent' : 'border-gray-200'}`}>
                                    <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full ring-4 ring-white ${log.action.includes('LOGIN') ? 'bg-blue-500' :
                                        log.action.includes('PAYMENT') ? 'bg-green-500' : 'bg-purple-500'
                                        }`}></div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                                            {new Date(log.created_at).toLocaleString()}
                                        </span>
                                        <div className="flex flex-col">
                                            <span className="text-gray-900 font-bold text-sm">{log.action.replace(/_/g, ' ')}</span>
                                            <span className="text-gray-500 text-xs mt-0.5">{log.details}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Column Stack */}
                    <div className="space-y-6">
                        {/* Video Watching History */}
                        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Continue Watching</h2>
                                <button className="text-xs font-bold text-blue-600 hover:text-blue-700">View All</button>
                            </div>
                            <div className="space-y-4">
                                {[
                                    { title: 'Integration Methods', course: 'Pure Mathematics', progress: 75 },
                                    { title: 'Newton`s Laws of Motion', course: 'Applied Mathematics', progress: 30 },
                                ].map((video, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/50 transition-colors cursor-pointer group">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                                            <FaBook className="text-sm" />
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="text-sm font-bold text-gray-900">{video.title}</h4>
                                            <p className="text-xs text-gray-500 mb-1.5">{video.course}</p>
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${video.progress}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Payment History (Mini) */}
                        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Recent Payments</h2>
                                <button className="text-xs font-bold text-blue-600 hover:text-blue-700">Manage</button>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { month: 'December 2025', amount: '2500', status: 'Paid', date: 'Dec 01' },
                                    { month: 'November 2025', amount: '2500', status: 'Paid', date: 'Nov 02' },
                                ].map((payment, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-green-50/50 border border-green-100">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                <FaCheckCircle className="text-xs" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-900">{payment.month}</h4>
                                                <p className="text-xs text-gray-500">LKR {payment.amount}</p>
                                            </div>
                                        </div>
                                        <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-lg">{payment.status}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
